import { useEffect, useState, useCallback, useRef } from 'react';
import { Table, message, Button, Tag, Modal, Input, Space } from 'antd';
import { ReloadOutlined, LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import Search from 'antd/es/input/Search';
import { Header } from '../header/header';
import { GetAccessToken } from '../../utils/auth-utils';
import { scopes } from '../../authConfig';
import { SalesManager, SalesChannel, ProductType, PaymentModel, Department, SalesLeague } from '../../dto/model';
import { SalesAgentsFilters, salesAgentsService } from '../../services/sales-agents-service';
import './sales-agents-page.css';
import { strings } from '../../lang';
import { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { SalesCallCalendar } from '../appointment-booking/sales-call-calendar';
import { FilterValue } from 'antd/es/table/interface';
import { specialSalesChannels } from '../../constants/special-sales-channels';
import { getSalesChannelLabel } from '../../utils/inbound-utils';

interface ISalesAgentsPageProps {
  searchInput: string | null;
}

const salesForceRootUrl = process.env.REACT_APP_SALESFORCE_BASE_URL;
const airtableBaseUrl = process.env.REACT_APP_AIRTABLE_BASE_URL;
const pageSize = 10;

export const SalesAgentsPage = ({ searchInput }: ISalesAgentsPageProps) => {
  const { instance, inProgress } = useMsal();
  const [salesAgents, setSalesAgents] = useState<SalesManager[]>([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState(searchInput || '');
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<SalesManager | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const regionSearchInputRef = useRef(null);
  const [regionFilterError, setRegionFilterError] = useState(false);
  const [tableFilters, setTableFilters] = useState<{
    salesChannel: SalesChannel[] | null;
    status: string[] | null;
    salesLeague: SalesLeague[] | null;
    department: Department[] | null;
    productType: ProductType[] | null;
    paymentModel: PaymentModel[] | null;
    regions: string | null;
  }>({
    salesChannel: null,
    status: null,
    salesLeague: null,
    department: null,
    productType: null,
    paymentModel: null,
    regions: null,
  });

  const fetchSalesAgents = useCallback(
    async (page: number, name: string) => {
      setIsSearchLoading(true);
      try {
        const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);

        const filters: SalesAgentsFilters = {
          name,
          salesChannels: tableFilters.salesChannel || undefined,
          isDeleted: tableFilters.status?.length === 2 ? undefined : tableFilters.status?.[0] === 'active' ? false : tableFilters.status?.[0] === 'inactive' ? true : undefined,
          salesLeagues: tableFilters.salesLeague || undefined,
          departments: tableFilters.department || undefined,
          productTypes: tableFilters.productType || undefined,
          paymentModels: tableFilters.paymentModel || undefined,
          regions: tableFilters.regions || undefined,
        };

        const result = await salesAgentsService.getSalesAgents(token.accessToken, page, pageSize, filters);

        if (result && result.isSuccess) {
          setSalesAgents(result.data);
          setTotalCount(result.pagination.totalCount);
        } else {
          message.error(strings.salesAgentManagement.failedToLoad);
        }
      } catch (error) {
        message.error(strings.salesAgentManagement.failedToLoad);
      }
      setIsSearchLoading(false);
    },
    [instance, inProgress, tableFilters]
  );

  useEffect(() => {
    fetchSalesAgents(currentPage, searchInputValue);
  }, [fetchSalesAgents, currentPage]);

  const refresh = () => {
    fetchSalesAgents(currentPage, searchInputValue);
  };

  const onSearch = (e: string) => {
    setCurrentPage(1);
    fetchSalesAgents(1, e);
  };

  const onViewCalendarClick = (agent: SalesManager) => {
    setSelectedAgent(agent);
    setShowCalendarModal(true);
  };

  const onChangeCalendar = (pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>) => {
    const newPage = pagination.current || 1;
    const newFilters = {
      salesChannel: filters.salesChannel as SalesChannel[],
      status: filters.status as string[],
      salesLeague: filters.salesLeague as SalesLeague[],
      department: filters.department as Department[],
      productType: filters.productType as ProductType[],
      paymentModel: filters.paymentModel as PaymentModel[],
      regions: (filters.regions && Array.isArray(filters.regions) ? filters.regions[0] : filters.regions) as string | null,
    };
    // Only update page if it actually changed
    if (currentPage !== newPage) {
      setCurrentPage(newPage);
    }
    // Only update filters if they actually changed
    if (JSON.stringify(tableFilters) !== JSON.stringify(newFilters)) {
      setTableFilters(newFilters);
    }
  };

  const columns: ColumnsType<SalesManager> = [
    {
      title: strings.salesAgentManagement.tableColumns.id,
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: strings.salesAgentManagement.tableColumns.name,
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: strings.salesAgentManagement.tableColumns.status,
      key: 'status',
      render: (record: SalesManager) => <Tag color={record.isDeleted ? 'red' : 'green'}>{record.isDeleted ? strings.inactiveIndicator : strings.activeIndicator}</Tag>,
      filters: [
        { text: strings.activeIndicator, value: 'active' },
        { text: strings.inactiveIndicator, value: 'inactive' },
      ],
      onFilter: (value, record) => (value === 'active' ? !record.isDeleted : record.isDeleted),
    },
    {
      title: 'Azure ID',
      dataIndex: 'azureId',
      key: 'azureId',
    },
    {
      title: 'Salesforce Link',
      dataIndex: 'salesForceLink',
      key: 'salesForceLink',
      render: (_, record) => {
        const url = record.salesChannel === SalesChannel.FieldSales ? `${airtableBaseUrl}/${record.externalAccountId}` : `${salesForceRootUrl}/lightning/r/User/${record.externalAccountId}/view`;
        const crm = record.salesChannel === SalesChannel.FieldSales ? 'Airtable' : 'SF';
        return (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {strings.salesAgentManagement.viewInCrm(crm)}
          </a>
        );
      },
    },
    {
      title: strings.salesAgentManagement.tableColumns.salesChannel,
      dataIndex: 'salesChannel',
      key: 'salesChannel',
      filters: [
        { text: 'Digital Sales', value: SalesChannel.DigitalSales },
        { text: 'Field Sales', value: SalesChannel.FieldSales },
        { text: 'CRM Sales', value: SalesChannel.CrmSales },
        { text: 'Revision', value: SalesChannel.Revision },
        { text: 'Sales Call 2', value: SalesChannel.SalesCall2 },
        { text: 'Lead Queue', value: SalesChannel.LeadQueue },
        { text: 'Retention Sales Call', value: SalesChannel.Retention },
        ...specialSalesChannels.map((value) => ({ text: getSalesChannelLabel(value), value })),
      ],
      onFilter: (value, record) => record.salesChannel === value,
    },
    {
      title: strings.salesAgentManagement.tableColumns.department,
      dataIndex: 'department',
      key: 'department',
      filters: [
        { text: 'Customer Service', value: Department.CustomerService },
        { text: 'IDNow', value: Department.IDNow },
        { text: 'Home Improvement', value: Department.HomeImprovement },
        { text: 'Sales Berlin', value: Department.SalesBerlin },
        { text: 'Sales Essen', value: Department.SalesEssen },
        { text: 'Sales Frankfurt', value: Department.SalesFrankfurt },
        { text: 'Sales Hamburg', value: Department.SalesHamburg },
        { text: 'Sales Leipzig', value: Department.SalesLeipzig },
        { text: 'Sales München', value: Department.SalesMunchen },
        { text: 'Sales Retention', value: Department.SalesRetention },
      ],
      onFilter: (value, record) => record.department === value,
    },
    {
      title: strings.salesAgentManagement.tableColumns.salesLeague,
      dataIndex: 'salesLeague',
      key: 'salesLeague',
      filters: [
        { text: '0', value: SalesLeague.Zero },
        { text: '1', value: SalesLeague.One },
        { text: '2', value: SalesLeague.Two },
        { text: '3', value: SalesLeague.Three },
        { text: '4', value: SalesLeague.Four },
        { text: '5', value: SalesLeague.Five },
        { text: '6', value: SalesLeague.Six },
        { text: '7', value: SalesLeague.Seven },
        { text: '8', value: SalesLeague.Eight },
        { text: '9', value: SalesLeague.Nine },
        { text: '10', value: SalesLeague.Ten },
        { text: '11', value: SalesLeague.Eleven },
        { text: '12', value: SalesLeague.Twelve },
        { text: '13', value: SalesLeague.Thirteen },
        { text: '14', value: SalesLeague.Fourteen },
      ],
      onFilter: (value, record) => record.salesLeague === value,
    },
    {
      title: strings.salesAgentManagement.tableColumns.zipCodes,
      dataIndex: 'regions',
      key: 'regions',
      render: (regions: string[]) => (regions && regions.length > 0 ? regions.join(', ') : ''),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
        const validateZipCodes = (value: string): boolean => {
          if (!value || !value.trim()) return true;
          const zipCodePattern = /^\d{5}(,\d{5})*$/;
          return zipCodePattern.test(value.trim());
        };

        const handleConfirm = () => {
          const currentValue = (selectedKeys[0] as string) || '';
          if (validateZipCodes(currentValue)) {
            setRegionFilterError(false);
            confirm();
          } else {
            setRegionFilterError(true);
          }
        };

        const handleReset = () => {
          setRegionFilterError(false);
          if (clearFilters) {
            clearFilters();
            confirm();
          }
        };

        const currentValue = (selectedKeys[0] as string) || '';

        return (
          <div style={{ padding: 8 }}>
            <Input
              ref={regionSearchInputRef}
              placeholder="12345 or 12345,67890"
              value={currentValue}
              onChange={(e) => {
                setRegionFilterError(false);
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={handleConfirm}
              style={{ marginBottom: 8, display: 'block' }}
              status={regionFilterError ? 'error' : undefined}
            />
            <Space>
              <Button type="primary" onClick={handleConfirm} icon={<SearchOutlined />} size="small" style={{ width: 90 }} />
              <Button onClick={handleReset} size="small" style={{ width: 90 }}>
                Reset
              </Button>
            </Space>
          </div>
        );
      },
      filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? 'var(--btn-primary)' : undefined }} />,
      onFilter: (_value, _record) => {
        return true;
      },
    },
    {
      title: strings.salesAgentManagement.tableColumns.lastModified,
      dataIndex: 'lastModifiedDate',
      key: 'lastModifiedDate',
      render: (date: Date) => new Date(date).toLocaleDateString(),
    },
    {
      title: strings.salesAgentManagement.tableColumns.productType,
      dataIndex: 'productType',
      key: 'productType',
      filters: [
        { text: 'HEATPUMP', value: ProductType.HEATPUMP },
        { text: 'PV', value: ProductType.PV },
        { text: 'ZEUS', value: ProductType.ZEUS },
        { text: 'COMBINED', value: ProductType.COMBINED },
      ],
      onFilter: (value, record) => record.productType === value,
      render: (productType: ProductType) => {
        const getProductTypeColor = (type: ProductType) => {
          switch (type) {
            case ProductType.PV:
              return 'blue';
            case ProductType.ZEUS:
              return 'purple';
            case ProductType.COMBINED:
              return 'orange';
            case ProductType.HEATPUMP:
              return 'green';
            case ProductType.UNKNOWN:
            default:
              return 'default';
          }
        };
        return <Tag color={getProductTypeColor(productType)}>{productType}</Tag>;
      },
    },
    {
      title: strings.salesAgentManagement.tableColumns.paymentModel,
      dataIndex: 'paymentModel',
      key: 'paymentModel',
      filters: [
        { text: 'Lease', value: PaymentModel.LEASE },
        { text: 'Loan', value: PaymentModel.LOAN },
      ],
      onFilter: (value, record) => record.paymentModel === value,
    },
    {
      title: strings.availability,
      key: 'availability',
      render: (_, record) => (
        <Button type="primary" className="view-calendar-btn" onClick={() => onViewCalendarClick(record)}>
          {strings.salesAgentManagement.viewSlots}
        </Button>
      ),
    },
  ];

  return (
    <>
      <Header
        centerComponents={
          <Search
            style={{ width: 608 }}
            value={searchInputValue}
            onChangeCapture={(e) => {
              setSearchInputValue(e.currentTarget.value);
            }}
            onSearch={(e) => onSearch(e)}
            enterButton
            loading={isSearchLoading}
            placeholder={strings.salesAgentManagement.searchPlaceholder}
          />
        }
      />
      <div className="sales-agents-container">
        <div className="sales-agents-header">
          <h2>{strings.salesAgentManagement.title}</h2>
          <Button type="default" onClick={refresh} icon={isSearchLoading ? <LoadingOutlined /> : <ReloadOutlined />} loading={isSearchLoading}>
            {strings.salesAgentManagement.refresh}
          </Button>
        </div>
        <Table
          dataSource={salesAgents}
          columns={columns}
          rowKey="id"
          loading={isSearchLoading}
          pagination={{
            pageSize,
            current: currentPage,
            total: totalCount,
            showSizeChanger: false,
          }}
          scroll={{ x: 1200 }}
          className="sales-agents-table"
          rowClassName={() => 'sales-agents-row'}
          onChange={onChangeCalendar}
        />
      </div>

      <Modal
        open={showCalendarModal}
        width="fit-content"
        height="fit-content"
        title={selectedAgent ? `${selectedAgent.firstName} ${selectedAgent.lastName}'s ${strings.salesAgentManagement.modalTitle.split(' ').pop()}` : strings.salesAgentManagement.modalTitle}
        onCancel={() => {
          setShowCalendarModal(false);
          setSelectedAgent(null);
        }}
        footer={null}
      >
        {selectedAgent && (
          <>
            <div className="sales-agent-modal-header">
              <div className="sales-agent-modal-department">
                {strings.salesAgentManagement.department} – {selectedAgent.department}
              </div>
            </div>

            <div className="bp-calendar" style={{ minWidth: 700 }}>
              <SalesCallCalendar opportunityId="" signupId="" sourceSystemHeader="" salesManagerId={selectedAgent.id.toString()} onBooking={() => {}} />
            </div>
          </>
        )}
      </Modal>
    </>
  );
};
